<template>
  <div>
    <div class="mobile-banner-wrapper" :class="{ 'is-hidden': bannerHidden }">
      <div class="mobile-top-banner" @click="handleBannerClick">
        <span class="banner-text" :class="{ 'is-pulsing': isPulsing }">Configure your label</span>
      </div>
    </div>
    <div ref="bottomBarRef" class="mobile-bottom-bar">
      <button
        v-for="section in sections"
        :key="section.key"
        type="button"
        class="mobile-bottom-bar-item"
        :class="{ 'is-active': activeSection === section.key, 'is-wiggling': isWiggling }"
        :aria-pressed="activeSection === section.key"
        :aria-label="`Open ${section.label} section`"
        @click="handleSectionClick(section.key)"
      >
        <span class="icon">
          <i :class="section.icon" aria-hidden="true"></i>
        </span>
        <span class="mobile-bottom-bar-label phone-label">{{ section.shortLabel }}</span>
        <span class="mobile-bottom-bar-label tablet-label">{{
          section.tabletLabel || section.shortLabel
        }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, onUnmounted } from "vue";

  const props = defineProps({
    activeSection: {
      type: String,
      default: null,
    },
  });

  const emit = defineEmits(["select"]);

  const isPulsing = ref(false);
  const bannerHidden = ref(false);
  const isWiggling = ref(false);
  const bottomBarRef = ref(null);
  let resizeObserver = null;

  const pendingTimers = new Set();

  function safeTimeout(callback, delay) {
    const timer = setTimeout(() => {
      pendingTimers.delete(timer);
      callback();
    }, delay);
    pendingTimers.add(timer);
    return timer;
  }

  onMounted(() => {
    isPulsing.value = true;
    safeTimeout(() => {
      isPulsing.value = false;
    }, 2400); // 1s delay + 1.4s animation duration

    window.addEventListener("resize", handleResize);

    // Monitor bottom bar height dynamic changes (text wrapping etc)
    if (window.ResizeObserver && bottomBarRef.value) {
      resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.target === bottomBarRef.value) {
            updateHeightVariable(entry.contentRect.height);
          }
        }
      });
      resizeObserver.observe(bottomBarRef.value);
      // Initial set
      updateHeightVariable(bottomBarRef.value.offsetHeight);
    }
  });

  onUnmounted(() => {
    window.removeEventListener("resize", handleResize);
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    pendingTimers.forEach((t) => clearTimeout(t));
    pendingTimers.clear();
  });

  function updateHeightVariable(height) {
    if (height && height > 0) {
      // Use offsetHeight from ref (includes padding)
      const el = bottomBarRef.value;
      const h = el ? el.offsetHeight : height;
      document.documentElement.style.setProperty("--mobile-bottom-bar-height", `${h}px`);
    }
  }

  function handleResize() {
    // Close panel if screen is desktop size (1024px+)
    if (window.innerWidth >= 1024 && props.activeSection) {
      emit("select", null);
    }
  }

  function handleBannerClick() {
    isWiggling.value = true;
    bannerHidden.value = true;
    safeTimeout(() => {
      isWiggling.value = false;
    }, 2400);
  }

  const sections = [
    {
      key: "serving",
      label: "Serving Information",
      shortLabel: "Serving",
      icon: "fas fa-utensils",
    },
    {
      key: "dietary-info",
      label: "Dietary Information",
      shortLabel: "Dietary",
      icon: "fa-duotone fa-chart-pie",
    },
    {
      key: "dv-lines",
      label: "Vitamins & Minerals",
      shortLabel: "Ingred. with DV",
      tabletLabel: "Ingredients with DV",
      icon: "fas fa-atom",
    },
    {
      key: "lines",
      label: "Ingredients without DVs",
      shortLabel: "Ingred. w/o DV",
      tabletLabel: "Ingredients w/o DV",
      icon: "far fa-plus-square",
    },
    {
      key: "blends",
      label: "Blends",
      shortLabel: "Blends",
      icon: "fas fa-object-group",
    },

    {
      key: "label-settings",
      label: "Label Settings",
      shortLabel: "Settings",
      tabletLabel: "Sections/Style",
      icon: "fa-duotone fa-sliders-h",
    },
  ];

  function handleSectionClick(key) {
    if (props.activeSection === key) {
      emit("select", null);
    } else {
      emit("select", key);
      bannerHidden.value = true;
    }
  }
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .mobile-bottom-bar {
    display: flex;
    align-items: stretch;
    justify-content: space-around;
    background: $info;
    padding: 0.25rem 0.5rem;
    gap: 4px;
  }

  .mobile-banner-wrapper {
    display: flex;
    justify-content: center;
    overflow: hidden;
    max-height: 100px;
    transition: max-height 0.4s ease-out;
    background: transparent;

    &.is-hidden {
      max-height: 0;
      transition: max-height 0.4s ease-in;
    }
  }

  .mobile-top-banner {
    background: $info;
    color: $white;
    text-align: center;
    padding: 0.375rem 1.5rem;
    padding-top: calc(0.375rem + 2px);
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    cursor: pointer;
    position: relative;
    top: 4px;

    &:hover {
      background: darken($info, 5%);
    }
  }

  .banner-text {
    display: inline-block;
    transform-origin: center center;

    &.is-pulsing {
      animation: elastic-wiggle 1.4s cubic-bezier(0.36, 0.07, 0.19, 0.97) 1;
      animation-delay: 1s;
      animation-fill-mode: both;
    }
  }

  @keyframes elastic-wiggle {
    0%,
    100% {
      transform: rotate(0deg);
    }
    10% {
      transform: rotate(-4deg);
    }
    20% {
      transform: rotate(3deg);
    }
    30% {
      transform: rotate(-2deg);
    }
    40% {
      transform: rotate(1deg);
    }
    50% {
      transform: rotate(0deg);
    }
  }

  .mobile-bottom-bar-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 2px;
    padding: 0.5rem 0.25rem;
    background: transparent;
    border: none;
    cursor: pointer;
    color: $white;
    transition:
      color 0.15s ease,
      background-color 0.15s ease;
    border-radius: 4px;
    min-height: 54px;

    .icon {
      font-size: 1.25rem;
      font-weight: 400;
    }

    &:hover {
      color: $white;
      background-color: rgba(black, 0.15);
    }

    &.is-active {
      color: $white;
      background-color: rgba(black, 0.25);
    }

    &.is-wiggling .icon {
      animation: wiggle 1.2s ease 2;
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid $white;
      outline-offset: -2px;
    }
  }

  @keyframes wiggle {
    0%,
    100% {
      transform: rotate(0deg) scale(1);
    }
    15% {
      transform: rotate(-5deg) scale(1.1);
    }
    30% {
      transform: rotate(5deg) scale(1.15);
    }
    45% {
      transform: rotate(-5deg) scale(1.1);
    }
    60% {
      transform: rotate(5deg) scale(1.05);
    }
    75% {
      transform: rotate(-3deg) scale(1.03);
    }
    85% {
      transform: rotate(3deg) scale(1.01);
    }
  }

  .mobile-bottom-bar-label {
    font-size: 0.625rem;
    font-weight: 700;
    text-align: center;
    line-height: 1.2;
    max-width: 100%;
    color: $white;

    &.phone-label {
      display: block;
    }

    &.tablet-label {
      display: none;
    }

    // Tablet screens: >= $tablet and < $desktop
    @media screen and (min-width: $tablet) and (max-width: ($desktop - 1px)) {
      &.phone-label {
        display: none;
      }

      &.tablet-label {
        display: block;
      }
    }
  }
</style>
